import React from 'react';
import {
  Tabs,
  Tab,
  TabsProps,
  Box,
  Typography,
  Badge,
} from '@mui/material';

/**
 * Design System Color Constants
 */
const COLORS = {
  ACCENT: '#005e5d',
  ACCENT_DARK: '#004d4c',
  ACCENT_LIGHT: '#005e5d14',
  SURFACE_SECONDARY: '#F2F3F5',
  BORDER: '#E6E7EB',
  BORDER_HOVER: '#D0D2D7',
  TEXT_PRIMARY: '#333333',
  TEXT_SECONDARY: '#666666',
  TEXT_TERTIARY: '#999999',
  DESTRUCTIVE: '#D32F2F',
  SUCCESS: '#49CC90',
  WARNING: '#FCA130',
  DISABLED: '#D0D2D7',
};

export interface TabItem {
  /**
   * Tab label text
   */
  label: string;
  /**
   * Tab value/id
   */
  value: string | number;
  /**
   * Optional badge count
   */
  badge?: number;
  /**
   * Icon element
   */
  icon?: React.ReactNode;
  /**
   * Show icon before label
   */
  iconPosition?: 'start' | 'end' | 'top' | 'bottom';
  /**
   * Disable this tab
   */
  disabled?: boolean;
  /**
   * Tab content
   */
  content?: React.ReactNode;
}

export interface BaseTabsProps
  extends Omit<TabsProps, 'children' | 'onChange' | 'value'> {
  /**
   * Array of tab items
   */
  items: TabItem[];
  /**
   * Currently active tab value
   */
  value: string | number;
  /**
   * Tab change handler
   */
  onChange?: (value: string | number) => void;
  /**
   * Tab orientation
   */
  orientation?: 'horizontal' | 'vertical';
  /**
   * Variant - scrollable or standard
   */
  variant?: 'standard' | 'scrollable';
  /**
   * Show tab content below tabs
   */
  showContent?: boolean;
  /**
   * Tab background color
   */
  backgroundColor?: string;
  /**
   * Tab size
   */
  size?: 'small' | 'medium' | 'large';
  /**
   * Color variant
   */
  colorVariant?: 'primary' | 'secondary' | 'success' | 'warning' | 'error';
  /**
   * Fullwidth tabs
   */
  fullWidth?: boolean;
}

export interface BaseTabPanelProps {
  /**
   * Panel value
   */
  value: string | number;
  /**
   * Current selected value
   */
  selectedValue: string | number;
  /**
   * Panel content
   */
  children?: React.ReactNode;
  /**
   * Additional padding
   */
  padding?: number;
}

/**
 * Get color based on variant
 */
const getTabColor = (
  colorVariant: 'primary' | 'secondary' | 'success' | 'warning' | 'error' = 'primary'
): string => {
  const colorMap = {
    primary: COLORS.ACCENT,
    secondary: COLORS.TEXT_SECONDARY,
    success: COLORS.SUCCESS,
    warning: COLORS.WARNING,
    error: COLORS.DESTRUCTIVE,
  };
  return colorMap[colorVariant];
};

/**
 * Get tab padding based on size
 */
const getTabPadding = (size: 'small' | 'medium' | 'large' = 'medium'): string => {
  const paddingMap = {
    small: '8px 12px',
    medium: '12px 16px',
    large: '16px 24px',
  };
  return paddingMap[size];
};

/**
 * BaseTabPanel Component
 *
 * Panel for tab content display.
 */
export const BaseTabPanel: React.FC<BaseTabPanelProps> = ({
  value,
  selectedValue,
  children,
  padding = 2,
}) => {
  return (
    <div
      role="tabpanel"
      hidden={value !== selectedValue}
      id={`tabpanel-${value}`}
      aria-labelledby={`tab-${value}`}
      style={{
        display: value === selectedValue ? 'block' : 'none',
      }}
    >
      {value === selectedValue && (
        <Box sx={{ p: padding }}>
          {children}
        </Box>
      )}
    </div>
  );
};

BaseTabPanel.displayName = 'BaseTabPanel';

/**
 * BaseTab Component
 *
 * Premium tab component following the Pulse API design system.
 * Supports tabs with icons, badges, and custom content.
 *
 * @example
 * ```tsx
 * const [activeTab, setActiveTab] = useState<string | number>('overview');
 *
 * const tabs: TabItem[] = [
 *   { label: 'Overview', value: 'overview' },
 *   { label: 'Settings', value: 'settings' },
 *   { label: 'Advanced', value: 'advanced', badge: 3 },
 * ];
 *
 * <BaseTabs
 *   items={tabs}
 *   value={activeTab}
 *   onChange={setActiveTab}
 *   showContent
 * />
 *
 * // With icons
 * <BaseTabs
 *   items={[
 *     { label: 'Dashboard', value: 'dash', icon: <DashboardIcon /> },
 *     { label: 'Users', value: 'users', icon: <PeopleIcon />, badge: 5 },
 *   ]}
 *   value={activeTab}
 *   onChange={setActiveTab}
 * />
 *
 * // With custom content
 * <BaseTabs
 *   items={[
 *     {
 *       label: 'Tab 1',
 *       value: 'tab1',
 *       content: <div>Content 1</div>,
 *     },
 *   ]}
 *   value={activeTab}
 *   onChange={setActiveTab}
 *   showContent
 * />
 * ```
 */
export const BaseTabs = React.forwardRef<HTMLDivElement, BaseTabsProps>(
  (
    {
      items,
      value,
      onChange,
      orientation = 'horizontal',
      variant = 'standard',
      showContent = false,
      backgroundColor,
      size = 'medium',
      colorVariant = 'primary',
      fullWidth = false,
      ...props
    },
    ref
  ) => {
    const handleChange = (event: React.SyntheticEvent, newValue: string | number) => {
      onChange?.(newValue);
    };

    const tabColor = getTabColor(colorVariant);
    const tabPadding = getTabPadding(size);

    return (
      <Box
        ref={ref}
        sx={{
          display: 'flex',
          flexDirection: orientation === 'horizontal' ? 'column' : 'row',
          gap: 0,
        }}
      >
        <Tabs
          value={value}
          onChange={handleChange}
          orientation={orientation}
          variant={variant}
          scrollButtons="auto"
          {...props}
          sx={{
            backgroundColor: backgroundColor || COLORS.SURFACE_SECONDARY,
            borderBottom: orientation === 'horizontal' ? `1px solid ${COLORS.BORDER}` : 'none',
            borderRight: orientation === 'vertical' ? `1px solid ${COLORS.BORDER}` : 'none',
            '& .MuiTabs-indicator': {
              backgroundColor: tabColor,
              height: orientation === 'horizontal' ? 3 : 3,
            },
            '& .MuiTab-root': {
              padding: tabPadding,
              minHeight: size === 'small' ? 32 : size === 'large' ? 56 : 44,
              color: COLORS.TEXT_SECONDARY,
              fontFamily: '"Manrope", sans-serif',
              fontSize: '13px',
              fontWeight: 500,
              textTransform: 'none',
              transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
              '&:hover': {
                backgroundColor: COLORS.ACCENT_LIGHT,
                color: COLORS.TEXT_PRIMARY,
              },
              '&.Mui-selected': {
                color: tabColor,
                fontWeight: 700,
              },
              '&.Mui-disabled': {
                color: COLORS.TEXT_TERTIARY,
                opacity: 0.6,
              },
            },
            ...(fullWidth && {
              '& .MuiTabs-flexContainer': {
                justifyContent: 'space-around',
              },
            }),
          }}
        >
          {items.map((item, index) => (
            <Tab
              key={index}
              value={item.value}
              disabled={item.disabled}
              label={
                <Box
                  sx={{
                    display: 'flex',
                    gap: 1,
                    alignItems: 'center',
                    justifyContent: 'center',
                  }}
                >
                  {item.icon && item.iconPosition !== 'end' && (
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                      {item.icon}
                    </Box>
                  )}
                  <Typography
                    sx={{
                      fontFamily: '"Manrope", sans-serif',
                      fontSize: 'inherit',
                      fontWeight: 'inherit',
                    }}
                  >
                    {item.label}
                  </Typography>
                  {item.icon && item.iconPosition === 'end' && (
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                      {item.icon}
                    </Box>
                  )}
                  {item.badge !== undefined && (
                    <Badge
                      badgeContent={item.badge}
                      sx={{
                        '& .MuiBadge-badge': {
                          backgroundColor: tabColor,
                          color: '#fff',
                          fontFamily: '"Manrope", sans-serif',
                          fontSize: '10px',
                          fontWeight: 600,
                          minWidth: '20px',
                          height: '20px',
                        },
                      }}
                    />
                  )}
                </Box>
              }
              id={`tab-${item.value}`}
              aria-controls={`tabpanel-${item.value}`}
            />
          ))}
        </Tabs>

        {showContent && (
          <Box
            sx={{
              flex: 1,
              overflow: 'auto',
            }}
          >
            {items.map((item, index) => (
              <BaseTabPanel
                key={index}
                value={item.value}
                selectedValue={value}
              >
                {item.content}
              </BaseTabPanel>
            ))}
          </Box>
        )}
      </Box>
    );
  }
);

BaseTabs.displayName = 'BaseTabs';

export default BaseTabs;
